import {
  AnthropicStreamHandler,
  AnthropicStreamOptions,
  StreamResult,
} from "./anthropic-stream-handler";

export type DisplayCallbacks = {
  onDisplayTextUpdate?: (visibleText: string) => void;
  onDisplayComplete?: (finalText: string) => void;
  // Fires when text content has finished displaying (typing complete). This is
  // different from the raw content_block_stop which occurs earlier.
  onTextContentBlockStop?: AnthropicStreamOptions["onTextContentBlockStop"];

  // Raw pass-through callbacks (fire immediately)
  onToolCallRequestStart?: AnthropicStreamOptions["onToolCallRequestStart"];
  onToolCallRequestUpdate?: AnthropicStreamOptions["onToolCallRequestUpdate"];
  onToolCallRequestComplete?: AnthropicStreamOptions["onToolCallRequestComplete"];
  onStreamComplete?: AnthropicStreamOptions["onComplete"];
  onError?: AnthropicStreamOptions["onError"];
  onEvent?: AnthropicStreamOptions["onEvent"];
};

export type TypingOptions = {
  charsPerSecond?: number; // base typing speed
  variance?: number; // randomness 0..1
  punctuationPauseMs?: number; // small pause after . , ; : ! ?
  minFrameMs?: number; // throttle UI updates (default ~16ms)
  accelerateWhenBacklogged?: boolean; // speed up if buffer large
  maxBacklogCharsForAcceleration?: number;
};

type AdapterOptions = {
  typingOpts?: TypingOptions;
  display?: DisplayCallbacks;
  startPaused?: boolean; // if true, the adapter will collect deltas but won't start typing until resumeDisplay()
};

export class DelayedStreamAdapter {
  private handler: AnthropicStreamHandler;

  // raw text buffers
  private inputBuffer = "";
  private visibleText = "";
  private inputEnded = false;
  private textStreamEnded = false;
  private textStopEmitted = false;

  // display scheduling
  private rafId: number | ReturnType<typeof setTimeout> | null = null;
  private lastEmitTs = 0;
  private paused = false;
  private useTimeoutScheduler: boolean;

  private minFrameMs: number;
  private punctuationPauseMs: number;
  private computeEmitCount: (dtMs: number) => number;

  constructor(private opts: AdapterOptions = {}) {
    const {
      charsPerSecond = 120,
      variance = 0.15,
      punctuationPauseMs = 120,
      minFrameMs = 16,
      accelerateWhenBacklogged = true,
      maxBacklogCharsForAcceleration = 400,
    } = opts.typingOpts || {};

    this.paused = !!opts.startPaused;
    this.minFrameMs = minFrameMs;
    this.punctuationPauseMs = punctuationPauseMs;
    this.useTimeoutScheduler =
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function";

    this.computeEmitCount = (dtMs: number) => {
      let cps = charsPerSecond;
      if (
        accelerateWhenBacklogged &&
        this.inputBuffer.length > maxBacklogCharsForAcceleration
      ) {
        cps = cps * 1.8; // simple boost when far behind
      }
      const jitter = 1 + (Math.random() * 2 - 1) * (variance ?? 0);
      const effectiveCps = Math.max(10, cps * jitter);
      const charsThisFrame = Math.floor((effectiveCps * dtMs) / 1000);
      return Math.max(0, charsThisFrame);
    };

    // Track content blocks to know when the text block ends
    const blockTypeByIndex = new Map<number, "text" | "tool_use">();

    this.handler = new AnthropicStreamHandler({
      onTextChunkDelta: (delta) => {
        this.inputBuffer += delta;
        this.ensureTick();
      },

      onToolCallRequestStart: this.opts.display?.onToolCallRequestStart,
      onToolCallRequestUpdate: this.opts.display?.onToolCallRequestUpdate,
      onToolCallRequestComplete: this.opts.display?.onToolCallRequestComplete,

      onComplete: (result) => {
        // Important: trigger stream-complete side effects first so chaining/tool calls
        // can proceed immediately without waiting for the typing/display loop.
        this.opts.display?.onStreamComplete?.(result);

        // Then mark input ended and let the display loop finish rendering the
        // remaining buffered text at its own pace.
        this.inputEnded = true;
        // If animations are disabled (no typing opts), flush instantly
        if (!this.opts.typingOpts) {
          this.flushDisplayNow();
        } else {
          this.ensureTick();
        }
      },

      onError: (err) => {
        this.opts.display?.onError?.(err);
      },

      onEvent: (eventType, data) => {
        try {
          if (eventType === "content_block_start") {
            const ev =
              data as import("./anthropic-stream-handler.types").ContentBlockStartEvent;
            if (ev.content_block.type === "text")
              blockTypeByIndex.set(ev.index, "text");
            if (ev.content_block.type === "tool_use")
              blockTypeByIndex.set(ev.index, "tool_use");
          } else if (eventType === "content_block_stop") {
            const ev =
              data as import("./anthropic-stream-handler.types").ContentBlockStopEvent;
            if (blockTypeByIndex.get(ev.index) === "text") {
              this.textStreamEnded = true;
              this.ensureTick();
            }
          }
        } catch {
          // ignore
        }
        this.opts.display?.onEvent?.(eventType, data);
      },
    });
  }

  async processStream(stream: ReadableStream): Promise<StreamResult> {
    this.resetDisplay();
    return this.handler.processStream(stream);
  }

  abort(): void {
    this.cancelTick();
    this.handler.abort();
  }

  cleanup(): void {
    this.cancelTick();
    this.handler.cleanup();
  }

  // Display control

  pauseDisplay(): void {
    this.paused = true;
    this.cancelTick();
  }

  resumeDisplay(): void {
    if (!this.paused) return;
    this.paused = false;
    this.ensureTick();
  }

  flushDisplayNow(): void {
    // Instantly reveal whatever we have and mark complete if input ended
    if (this.inputBuffer.length > 0) {
      this.visibleText += this.inputBuffer;
      this.inputBuffer = "";
      this.opts.display?.onDisplayTextUpdate?.(this.visibleText);
    }
    // Emit text stop once the text stream ended and all text is shown
    if (
      this.textStreamEnded &&
      !this.textStopEmitted &&
      this.inputBuffer.length === 0
    ) {
      this.textStopEmitted = true;
      this.opts.display?.onTextContentBlockStop?.();
    }
    if (this.inputEnded) {
      this.opts.display?.onDisplayComplete?.(this.visibleText);
      this.cancelTick();
    }
  }

  // Internals

  private ensureTick(): void {
    if (this.paused) return;
    if (this.rafId !== null) return;

    const loop = (now: number) => {
      if (this.paused) {
        this.rafId = null;
        return;
      }

      if (now - this.lastEmitTs < this.minFrameMs) {
        this.rafId = this.schedule(loop);
        return;
      }

      const dt = this.lastEmitTs ? now - this.lastEmitTs : this.minFrameMs;
      this.lastEmitTs = now;

      let emitCount = this.computeEmitCount(dt);
      emitCount = Math.min(emitCount, this.inputBuffer.length);

      if (emitCount > 0) {
        const slice = this.inputBuffer.slice(0, emitCount);
        this.inputBuffer = this.inputBuffer.slice(emitCount);
        this.visibleText += slice;
        this.opts.display?.onDisplayTextUpdate?.(this.visibleText);

        const lastChar = slice[slice.length - 1];
        if (/[.,;:!?]/.test(lastChar)) {
          this.lastEmitTs += this.punctuationPauseMs;
        }
      }

      // If text stream ended and we've rendered all text, emit stop
      if (
        this.textStreamEnded &&
        !this.textStopEmitted &&
        this.inputBuffer.length === 0
      ) {
        this.textStopEmitted = true;
        this.opts.display?.onTextContentBlockStop?.();
      }

      const doneTyping = this.inputEnded && this.inputBuffer.length === 0;

      if (doneTyping) {
        this.opts.display?.onDisplayComplete?.(this.visibleText);
        this.rafId = null;
        return;
      }

      this.rafId = this.schedule(loop);
    };

    this.rafId = this.schedule(loop);
  }

  private cancelTick(): void {
    if (this.rafId !== null) {
      if (this.useTimeoutScheduler) {
        clearTimeout(this.rafId as ReturnType<typeof setTimeout>);
      } else {
        cancelAnimationFrame(this.rafId as number);
      }
      this.rafId = null;
    }
  }

  private schedule(
    loop: (now: number) => void
  ): number | ReturnType<typeof setTimeout> {
    if (this.useTimeoutScheduler) {
      // Use setTimeout with minFrameMs to approximate a frame
      return setTimeout(() => loop(Date.now()), this.minFrameMs);
    }
    return requestAnimationFrame(loop);
  }

  private resetDisplay(): void {
    this.cancelTick();
    this.inputBuffer = "";
    this.visibleText = "";
    this.inputEnded = false;
    this.lastEmitTs = 0;
    // keep paused flag as is
  }
}
